import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { HierarchyControl, HierarchyControlItem } from "../../../../basf-ui";
import { useDictionary, useParameter } from "../../../shared";
import { first, orderBy, sortBy } from "lodash";
import { dataSourcesService, dictionaryService } from "../../../dataFlow";
import { SLIDE_NUMBER } from "../constants";
import { equal } from "../../../../../shared";

interface ControlItem extends HierarchyControlItem<number> {
  olapId: number;
}

const BUSINESS_UNIT = 1;
export const CommoditiesControl: React.FC = () => {
  const [controlItems, setItems] = useState<ControlItem[]>([]);
  const [loadingValue, setLoadingValue] = useState<boolean>(true);

  const [commodityIds, setCommodityIds] = useParameter<number[]>("@commodities");
  const [selectedCommodityId, selectCommodityId] = useParameter<number>("@selected_commodity_id");
  const [displayCommodityIds, setDisplayCommodityIds] = useParameter<number[]>("@commodities_display");

  const [dictItems, , loadingDict] = useDictionary(dictionaryService, "slides-commodities-display", undefined, {
    business_unit: BUSINESS_UNIT,
    slide: SLIDE_NUMBER,
  });

  useEffect(
    function updateControlItems() {
      setLoadingValue(true);
      if (dictItems) {
        const parents = dictItems.filter((item) => item.getValue("parent") == null);
        const childs = dictItems.filter((item) => item.getValue("parent") != null);
        const sortedParentsItems = orderBy(parents, (item) => item.getValue<string>("name"), ["desc"]);
        const sortedChildsItems = orderBy(childs, (item) => item.getValue<string>("name"), ["desc"]);
        const sortedDictItems = sortedParentsItems.concat(sortedChildsItems);

        const nextControlItems = sortedDictItems.map<ControlItem>((dictItem) => ({
          id: dictItem.getValue<number>("id")!,
          name: dictItem.getValue<Record<number, string>>("name")![9],
          parent: dictItem.getValue<number>("parent"),
          olapId: dictItem.getValue<number>("olap_commodity")!,
        }));
        setItems(nextControlItems);

        const selectedCandidates = nextControlItems.filter((e) => commodityIds?.includes(e.olapId));
        if (selectedCandidates) {
          const possibleIds = selectedCandidates.map((candidate) => candidate.id);
          if (!equal(possibleIds?.sort(), displayCommodityIds?.sort())) {
            setDisplayCommodityIds(possibleIds);
          }
        } else {
          setDisplayCommodityIds([nextControlItems[0]?.id]);
        }
        setLoadingValue(false);
      }
    },
    [dictItems]
  );

  const handlerChange = useCallback(
    async (value: number[]) => {
      if (!equal(displayCommodityIds, value)) {
        const selectedCandidates = controlItems.filter((e) => value.includes(e.id))!;
        setDisplayCommodityIds(value);

        const newCommodityIds = selectedCandidates.map((e) => e.olapId);
        const commodityIdsAndSources = await Promise.all(
          newCommodityIds.map(async (id) => {
            const source = await dataSourcesService.getDataSources(SLIDE_NUMBER, 1, Number(id));
            return {
              id,
              source,
            };
          })
        );
        const sortedCommodityIdsAndSources = sortBy(commodityIdsAndSources, (item) => item.source);
        const sortedCommodityIds = sortedCommodityIdsAndSources.map((item) => item.id);
        setCommodityIds(sortedCommodityIds);

        if (newCommodityIds.indexOf(Number(selectedCommodityId)) === -1) {
          const id = Number(first(sortedCommodityIds));
          selectCommodityId(id);
        }
      }
    },
    [controlItems, displayCommodityIds, selectedCommodityId]
  );

  const loading = loadingValue || loadingDict;

  const label = "Commodities";
  return (
    <HierarchyControl
      noSelectableParent
      multiple
      large
      loading={loading}
      label={label}
      value={displayCommodityIds!}
      items={controlItems}
      onChange={handlerChange}
    />
  );
};
